import { Suspense, useState } from "react";
import { IoClose } from "react-icons/io5";
import { Outlet, useNavigate, useOutletContext, useParams } from "react-router";
import styled from "styled-components";

import useApplicationsForExports from "../../../../clients/documentExports/useApplicationsForExports";
import useBrandsOfUser from "../../../../clients/users/useBrandsOfUser";
import * as UI from "../../../../modules/ui-kit";

import type { TemplateMetas, UsageOption } from "../../../../clients/documentExports/api";
import type { DocumentExportsOutletContext } from "../../index";

const Styled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    transition: opacity 200ms;

    ${ UI.HeroInput } {
        font-size: 2.4em;
    }

    ol {
        display: flex;
        flex-direction: column;
        gap: 8px;
        list-style: none;
        margin: 0;
        padding: 0;

        a {
            color: #000;
            font-size: 1.2rem;
            overflow: hidden;
            position: relative;
            text-decoration: none;
            text-overflow: ellipsis;
            white-space: nowrap;

            ${ UI.FluidAccent2 }

            &:hover {
                text-decoration: none;
            }

            &.current {
                font-weight: bold;
            }
        }

        .domain {
            color: #889;
            font-size: .9rem;
            margin-left: 6px;
        }

        .marker {
            color: #4dabf7;
            font-size: .7rem;
            font-weight: bold;
            margin-left: 8px;
            text-transform: uppercase;
        }
    }
`;

export default function Applications() {
    const [token, setToken] = useState("");
    const { applicationDomain } = useParams();
    const context = useOutletContext<DocumentExportsOutletContext>();

    return (
        <>
            <Styled>
                <UI.HeroInputField>
                    <div>
                        <UI.HeroInput
                            autoFocus={ !applicationDomain }
                            onChange={ setToken }
                            placeholder="Search"
                            value={ token }
                        />
                        <button onClick={ () => setToken("") } type="reset">
                            <IoClose />
                        </button>
                    </div>
                    <span>search Applications by domain or name</span>
                </UI.HeroInputField>
                <Suspense fallback={ <span>loading</span> }>
                    <ApplicationList templates={ context.templates } token={ token } usages={ context.usages } />
                </Suspense>
            </Styled>
            <Outlet context={ context } />
        </>
    );
}

function ApplicationList({ templates, token, usages }: { templates: TemplateMetas, token: string, usages: UsageOption[] }) {
    const { applicationDomain } = useParams();
    const navigate = useNavigate();
    const applications = useApplicationsForExports();
    const brandsOfUser = useBrandsOfUser();
    const needle = token.toLowerCase();

    // Templates belong to applications; access follows PIM's product-editor roles on the
    // application's brands ("*" = all). The service applies the same rule.
    const visible = applications
        .filter((application) =>
            brandsOfUser.includes("*")
            || (application.brandIDs ?? []).some((brandID) => brandsOfUser.includes(brandID)))
        .filter((application) =>
            [
                application.domain.toLowerCase().includes(needle),
                application.name?.toLowerCase().includes(needle),
            ].some(Boolean))
        .sort((a, b) => (a.name ?? a.domain).localeCompare(b.name ?? b.domain));

    if (!visible.length) return <span>no applications</span>;

    return (
        <ol>
            { visible.map((application) => (
                <li key={ application.id }>
                    <a
                        className={ application.domain === applicationDomain ? "current" : undefined }
                        href="#"
                        onClick={ (event) => {
                            event.preventDefault();
                            if (application.domain !== applicationDomain) navigate(application.domain);
                        } }
                    >
                        { application.name }
                        <span className="domain">[{ application.domain }.]</span>
                        { usages
                            .filter((usage) => templates[application.domain.toLowerCase()]?.[usage.value])
                            .map((usage) => <span className="marker" key={ usage.value }>{ usage.label }</span>) }
                    </a>
                </li>
            )) }
        </ol>
    );
}
